import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import clsx from 'clsx';
import { graphql } from 'gatsby';

import { Section } from '~components/Section';
import { ButtonLink } from '~components/ButtonLink';
import { Heading } from '~components/Heading';
import { RawContentOptions, rawContentToReactComponents } from '~utils/rawContentToReactComponents';
import Checkmark from './assets/check-circle.svg';
import * as styles from './ComponentCoverageLandingBody.module.css';

import type { ContentBlock } from '~components/types';
import type { Theme } from '~models/contentful';

type Props = {
  className?: string;
  content: ContentfulComponentCoverageLandingBody;
  isHeader?: boolean;
};

function parseButton(rawContent: string) {
  const buttonContentOverride: RawContentOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children): JSX.Element => <>{children}</>,
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <ButtonLink href={node.data.uri} className={styles.button}>
            {children}
          </ButtonLink>
        );
      },
    },
  };
  return rawContentToReactComponents(rawContent, buttonContentOverride);
}

const contentOverrides = (content: ContentfulComponentCoverageLandingBody): RawContentOptions => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children): JSX.Element => <Heading className={styles.title}>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children): JSX.Element => (
      <Heading level={2} className={styles.subtitle}>
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_, children): JSX.Element => <p className={styles.copy}>{children}</p>,
    [BLOCKS.UL_LIST]: (_, children): JSX.Element => <ul className={styles.uspList}>{children}</ul>,
    [BLOCKS.LIST_ITEM]: (_, children): JSX.Element => (
      <li className={styles.uspItem}>
        <Checkmark />
        {children}
      </li>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node): React.ReactNode | undefined => {
      const rawButtonContent = content.body.references.find((ref) => ref.contentful_id === node.data.target.sys.id)
        ?.body.raw;
      return rawButtonContent ? parseButton(rawButtonContent) : <></>;
    },
  },
});

export const ComponentCoverageLandingBody: ContentBlock<Props> = ({ content, isHeader = false, ...sectionProps }) => (
  <Section {...sectionProps} className={clsx(styles.section, isHeader && styles.header)}>
    {rawContentToReactComponents(content.body.raw, contentOverrides(content))}
    {content.smallCopy && <div className={styles.smallCopy}>{rawContentToReactComponents(content.smallCopy.raw)}</div>}
  </Section>
);

export type ContentfulComponentCoverageLandingBody = {
  __typename: 'ContentfulComponentCoverageLandingBody';
  contentfulid: string;
  body: {
    raw: string;
    references: {
      contentful_id: string;
      body: {
        raw: string;
      };
    }[];
  };
  smallCopy?: {
    raw: string;
  };
  theme?: Theme;
};

export const query = graphql`
  fragment ComponentCoverageLandingBody on ContentfulComponentCoverageLandingBody {
    __typename
    contentfulid
    body {
      raw
      references {
        contentful_id
        body {
          raw
        }
      }
    }
    smallCopy {
      raw
    }
    theme
  }
`;
